/* global Word */

/**
 * DocPilot AI — Taskpane Integration Module (v4)
 *
 * What's new vs v3:
 * - Uses deepAnalyzeDocument() — runs analyze + structure + terminology + crossref in parallel
 * - Per-stage progress: Style → Structure → Terminology → Cross-references → Merge
 * - New issue groups: Structure, Terminology, Cross-References
 * - Source badges on each issue card so users see WHY it was flagged
 * - Partial-failure tolerance: if one endpoint fails, others still render
 * - Existing single-issue auto-fix still works for analyze-source issues
 */

import { extractDocumentStructure, getLastExtractedJSON } from '../runs/docExtractor';
import {
  checkBackendHealth, deepAnalyzeDocument, generateReport,
  AIIssue, AISuggestion, AIConsistencyWarning, AIDeepAnalysisResult, AIReportResult
} from './aiService';

// ── State ──
let dpEnabled = false;
let dpConnected = false;
let dpLastDeepAnalysis: AIDeepAnalysisResult | null = null;
let dpLastReportResult: AIReportResult | null = null;
let dpRunFunctions: Map<string, () => Promise<any>> | null = null;
let dpHasAnalyzed = false;
let dpAnalysisStale = false;

export function invalidateAnalysis(): void {
  if (dpHasAnalyzed) {
    dpAnalysisStale = true;
    console.log("[DocPilot] Analysis marked stale — document was modified by a check");
    const banner = document.getElementById("dpStaleBanner");
    if (banner) banner.style.display = "flex";
  }
}

const DP_CHECK_NAMES: Record<string, string> = {
  run1:"Convert Font to TNR",run2:"Page Margins",run3:"Table Caption & Header",run4:"Non-Breaking Hyphens",
  run5:"Remove Extra Spaces",run6:"Body Text Styling",run7:"Body List & Bullet Styling",
  run8:"Figure & Caption Styling",run9:"Figure Footnote Text",run10:"Heading Styling",
  run11:"Table Text",run12:"Table Footnote",run13:"List of Tables",run14:"List of Figures",
  run15:"List of Abbreviations",run16:"Appendix Styling",run17:"Paragraph Formatting",
  run18:"Header Format",run19:"Footer Format",run20:"Consistent Header",run21:"Remove Footer",
  run22:"Add Confidential",run23:"Section Spacing",run24:"Cross-Reference Validation",
  run25:"Table List & Bullet Styling",run26:"Table Defaults"
};

// ── Issue groups: now include source-aware groups for deep-analysis findings ──
const ISSUE_GROUPS: { label: string; runs: number[]; sources?: string[]; icon: string }[] = [
  { label: "Structure Issues",          runs: [], sources: ["structure"], icon: "S" },
  { label: "Cross-Reference Issues",    runs: [24], sources: ["crossref"], icon: "X" },
  { label: "Terminology Issues",        runs: [], sources: ["terminology"], icon: "T" },
  { label: "Heading Issues",            runs: [10, 23], icon: "H" },
  { label: "Body Text Issues",          runs: [6], icon: "B" },
  { label: "List & Bullet Issues",      runs: [7, 25], icon: "#" },
  { label: "Table Issues",              runs: [3, 11, 26], icon: "T" },
  { label: "Figure & Caption Issues",   runs: [8, 9], icon: "F" },
  { label: "Footnote Issues",           runs: [12], icon: "f" },
  { label: "Font & Spacing Issues",     runs: [1, 2, 5, 4], icon: "A" },
  { label: "Header & Footer Issues",    runs: [18, 19, 20, 21, 22], icon: "H" },
  { label: "Other Structure",           runs: [13, 14, 15, 16, 17], icon: "?" },
  { label: "Other Issues",              runs: [], icon: "?" }
];

function getGroupForIssue(issue: AIIssue): string {
  // Source-based grouping wins (structure/terminology/crossref issues stay together)
  if (issue.source) {
    for (const g of ISSUE_GROUPS) {
      if (g.sources && g.sources.includes(issue.source)) return g.label;
    }
  }
  // Fall back to run number
  const runNum = issue.relatedRun || 0;
  for (const g of ISSUE_GROUPS) {
    if (g.runs.includes(runNum)) return g.label;
  }
  return "Other Issues";
}

// ═══════════════════════════════════════════════════════════════
// NAVIGATE TO PARAGRAPH IN WORD
// ═══════════════════════════════════════════════════════════════

async function navigateToParagraph(paraIndex: number): Promise<void> {
  try {
    await Word.run(async (context) => {
      const paras = context.document.body.paragraphs;
      paras.load("items");
      await context.sync();
      const total = paras.items.length;
      if (paraIndex < 0 || paraIndex >= total) return;
      try {
        paras.items[paraIndex].select();
        await context.sync();
        return;
      } catch (e) { /* fall through */ }
      for (let offset = 1; offset <= 3; offset++) {
        for (const delta of [-offset, offset]) {
          const tryIdx = paraIndex + delta;
          if (tryIdx >= 0 && tryIdx < total) {
            try {
              paras.items[tryIdx].select();
              await context.sync();
              return;
            } catch (e) { /* try next */ }
          }
        }
      }
    });
  } catch (err) {
    console.warn("[DocPilot] Navigate error:", err);
  }
}

// ═══════════════════════════════════════════════════════════════
// SINGLE-ISSUE AUTOFIX — applies style to ONE paragraph
// ═══════════════════════════════════════════════════════════════

async function fixSingleIssue(paraIndex: number, expectedStyle: string): Promise<boolean> {
  try {
    let fixed = false;
    await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      await context.sync();
      const paras = context.document.body.paragraphs;
      paras.load("items");
      await context.sync();
      if (paraIndex >= 0 && paraIndex < paras.items.length) {
        try {
          paras.items[paraIndex].style = expectedStyle;
          await context.sync();
          fixed = true;
        } catch (e) {
          console.error(`[DocPilot] Could not fix para ${paraIndex}:`, e);
        }
      }
      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();
    });
    return fixed;
  } catch (err) {
    console.error("[DocPilot] Fix error:", err);
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════
// INITIALIZATION
// ═══════════════════════════════════════════════════════════════

export function initDocPilot(runFunctions?: Map<string, () => Promise<any>>): void {
  dpRunFunctions = runFunctions || null;

  const toggle = document.getElementById("dpToggle") as HTMLInputElement;
  if (toggle) toggle.onchange = () => { dpEnabled = toggle.checked; onToggleChanged(); };

  const analyzeBtn = document.getElementById("dpAnalyzeBtn");
  if (analyzeBtn) analyzeBtn.onclick = onAnalyzeClick;

  const reportBtn = document.getElementById("dpReportBtn");
  if (reportBtn) reportBtn.onclick = onReportClick;

  const jsonBtn = document.getElementById("dpJsonBtn");
  if (jsonBtn) jsonBtn.onclick = showJSONPanel;

  const resultsClose = document.getElementById("dpResultsCloseBtn");
  if (resultsClose) resultsClose.onclick = () => {
    const p = document.getElementById("dp-results-panel"); if (p) p.style.display = "none";
  };
  const reportClose = document.getElementById("dpReportCloseBtn");
  if (reportClose) reportClose.onclick = () => {
    const p = document.getElementById("dp-report-panel"); if (p) p.style.display = "none";
  };
  const jsonClose = document.getElementById("dpJsonCloseBtn");
  if (jsonClose) jsonClose.onclick = () => {
    const p = document.getElementById("dp-json-panel"); if (p) p.style.display = "none";
  };

  document.querySelectorAll(".dp-filter-btn").forEach((btn: HTMLElement) => {
    btn.onclick = () => {
      document.querySelectorAll(".dp-filter-btn").forEach((b: HTMLElement) => b.classList.remove("active"));
      btn.classList.add("active");
      filterIssues(btn.getAttribute("data-filter") || "all");
    };
  });

  console.log("[DocPilot] ✓ Initialized (v4 — deep analysis)");
}

// ═══════════════════════════════════════════════════════════════
// TOGGLE / CONNECTION
// ═══════════════════════════════════════════════════════════════

async function onToggleChanged(): Promise<void> {
  const statusBar = document.getElementById("dpStatusBar");
  const actions = document.getElementById("dpActions");
  if (!dpEnabled) {
    if (statusBar) statusBar.style.display = "none";
    if (actions) actions.style.display = "none";
    return;
  }
  if (statusBar) statusBar.style.display = "flex";
  if (actions) actions.style.display = "flex";
  await checkConnection();
}

async function checkConnection(): Promise<void> {
  setStatus("checking", "Connecting...");
  try {
    const health = await checkBackendHealth();
    if (health.ok) {
      dpConnected = true;
      const ver = health.version ? ` (${health.version})` : "";
      setStatus("connected", `Connected — ${health.model || "AI"}${ver}`);
      setButtonsEnabled(true);
    } else {
      dpConnected = false;
      setStatus("disconnected", "Server not responding");
      setButtonsEnabled(false);
    }
  } catch {
    dpConnected = false;
    setStatus("disconnected", "Cannot reach localhost:3001");
    setButtonsEnabled(false);
  }
}

function setStatus(state: string, text: string): void {
  const dot = document.getElementById("dpStatusDot");
  const textEl = document.getElementById("dpStatusText");
  if (dot) dot.className = "dp-status-dot " + state;
  if (textEl) textEl.textContent = text;
}

function setButtonsEnabled(enabled: boolean): void {
  ["dpAnalyzeBtn", "dpReportBtn", "dpJsonBtn"].forEach(id => {
    const btn = document.getElementById(id) as HTMLButtonElement;
    if (btn) btn.disabled = !enabled;
  });
}

// ═══════════════════════════════════════════════════════════════
// ANALYZE — uses deepAnalyzeDocument() with multi-stage progress
// ═══════════════════════════════════════════════════════════════

async function onAnalyzeClick(): Promise<void> {
  if (dpHasAnalyzed && dpLastDeepAnalysis) {
    const panel = document.getElementById("dp-results-panel");
    if (panel) { panel.style.display = "block"; return; }
  }
  await runAnalysis();
}

async function runAnalysis(): Promise<void> {
  if (!dpConnected) { await checkConnection(); if (!dpConnected) return; }

  const progressEl = document.getElementById("dpProgress");
  try {
    if (progressEl) progressEl.style.display = "block";
    setButtonsEnabled(false);
    updateProgress(5, "Extracting document structure...");

    const docStructure = await extractDocumentStructure();
    updateProgress(15, `Extracted ${docStructure.paragraphs.length} paragraphs`);

    // Track which stages have completed for progress percentage
    const stageDone: Record<string, boolean> = {
      analyze: false, structure: false, terminology: false, crossref: false
    };

    const result = await deepAnalyzeDocument(docStructure, (stage, _stagePct, msg) => {
      // Compute overall pct: 15% baseline + 80% across 4 stages + 5% final merge
      if (stage === "merge") {
        updateProgress(95, msg);
        return;
      }
      stageDone[stage] = true;
      const completedStages = Object.values(stageDone).filter(Boolean).length;
      const overallPct = 15 + Math.round((completedStages / 4) * 80);
      updateProgress(overallPct, msg);
    });

    updateProgress(100, "Analysis complete!");

    dpLastDeepAnalysis = result;
    dpHasAnalyzed = true;
    dpAnalysisStale = false;

    const analyzeBtn = document.getElementById("dpAnalyzeBtn");
    if (analyzeBtn) {
      const s = analyzeBtn.querySelector("span");
      if (s) s.textContent = "View / Re-Analyze";
    }

    setTimeout(() => {
      if (progressEl) progressEl.style.display = "none";
      setButtonsEnabled(true);
      showDeepAnalysisResults(result);
    }, 500);

  } catch (err) {
    console.error("[DocPilot] Analysis error:", err);
    if (progressEl) progressEl.style.display = "none";
    setButtonsEnabled(true);
    const panel = document.getElementById("dp-results-panel");
    const content = document.getElementById("dpResultsContent");
    if (panel && content) {
      content.innerHTML =
        '<div class="dp-empty-state"><div class="dp-empty-icon">&#9888;&#65039;</div>' +
        '<div class="dp-empty-title">Analysis Failed</div>' +
        '<div class="dp-empty-desc">' + esc((err as Error).message || "Unknown error") + '</div></div>';
      panel.style.display = "block";
    }
  }
}

function updateProgress(pct: number, text: string): void {
  const f = document.getElementById("dpProgressFill");
  const t = document.getElementById("dpProgressText");
  if (f) f.style.width = pct + "%";
  if (t) t.textContent = text;
}

// ═══════════════════════════════════════════════════════════════
// RENDER DEEP ANALYSIS RESULTS — grouped, with source badges
// ═══════════════════════════════════════════════════════════════

function showDeepAnalysisResults(result: AIDeepAnalysisResult): void {
  const panel = document.getElementById("dp-results-panel");
  const content = document.getElementById("dpResultsContent");
  if (!panel || !content) return;

  const issues = result.merged.issues || [];
  const summary = result.merged.summary;

  let html = "";

  // Stale warning banner (hidden by default)
  html += '<div id="dpStaleBanner" class="dp-stale-banner" style="display:none">';
  html += '<span>&#9888;&#65039; Document was modified since this analysis. Paragraph positions may have shifted.</span>';
  html += '<button id="dpStaleReanalyze" class="dp-stale-btn">Re-Analyze Now</button>';
  html += '</div>';

  // Endpoint failures (if any)
  if (result.errors.length > 0) {
    html += '<div class="dp-stale-banner" style="background:#fff7ed;border-color:#fdba74;color:#9a3412">';
    html += '<span>&#9888;&#65039; Some checks failed: ';
    html += result.errors.map(e => esc(e.endpoint) + ' (' + esc(e.message.slice(0, 60)) + ')').join(', ');
    html += '</span></div>';
  }

  // Meta bar
  html += '<div class="dp-meta-bar">';
  html += '<span>Analyzed <strong>' + (result.meta?.paragraphCount || "?") + '</strong> paragraphs</span>';
  html += '<span>' + (result.meta?.totalElapsedSeconds?.toFixed(1) || "?") + 's</span>';
  html += '</div>';

  // Stats by severity
  const crit = summary.bySeverity.critical;
  const warn = summary.bySeverity.warning;
  const info = summary.bySeverity.info;
  const auto = summary.autoFixableCount;
  html += '<div class="dp-summary-bar">';
  html += '<div class="dp-summary-stat"><span class="dp-summary-stat-num dp-stat-critical">' + crit + '</span><span class="dp-summary-stat-label">Critical</span></div>';
  html += '<div class="dp-summary-stat"><span class="dp-summary-stat-num dp-stat-warning">' + warn + '</span><span class="dp-summary-stat-label">Warning</span></div>';
  html += '<div class="dp-summary-stat"><span class="dp-summary-stat-num dp-stat-info">' + info + '</span><span class="dp-summary-stat-label">Info</span></div>';
  html += '<div class="dp-summary-stat"><span class="dp-summary-stat-num dp-stat-auto">' + auto + '</span><span class="dp-summary-stat-label">Auto-fix</span></div>';
  html += '</div>';

  // Per-source breakdown
  html += '<div class="dp-summary-bar">';
  html += '<div class="dp-summary-stat" title="Per-paragraph style issues"><span class="dp-summary-stat-num">' + summary.bySource.analyze + '</span><span class="dp-summary-stat-label">Style</span></div>';
  html += '<div class="dp-summary-stat" title="Heading hierarchy & orphan captions"><span class="dp-summary-stat-num">' + summary.bySource.structure + '</span><span class="dp-summary-stat-label">Structure</span></div>';
  html += '<div class="dp-summary-stat" title="Acronym & terminology consistency"><span class="dp-summary-stat-num">' + summary.bySource.terminology + '</span><span class="dp-summary-stat-label">Terminology</span></div>';
  html += '<div class="dp-summary-stat" title="Broken or ambiguous cross-references"><span class="dp-summary-stat-num">' + summary.bySource.crossref + '</span><span class="dp-summary-stat-label">Cross-Refs</span></div>';
  html += '</div>';

  // Overview from primary analyze endpoint
  if (result.analyze?.analysis?.summary?.overview) {
    html += '<div class="dp-report-executive">' + esc(result.analyze.analysis.summary.overview) + '</div>';
  }

  // GROUPED ISSUES
  if (issues.length > 0) {
    const grouped: Map<string, AIIssue[]> = new Map();
    for (const issue of issues) {
      const group = getGroupForIssue(issue);
      if (!grouped.has(group)) grouped.set(group, []);
      grouped.get(group)!.push(issue);
    }
    for (const gDef of ISSUE_GROUPS) {
      const gIssues = grouped.get(gDef.label);
      if (!gIssues || gIssues.length === 0) continue;
      html += '<div class="dp-issue-group" data-group="' + esc(gDef.label) + '">';
      html += '<div class="dp-group-header">';
      html += '<span class="dp-group-icon">' + gDef.icon + '</span>';
      html += '<span class="dp-group-title">' + esc(gDef.label) + '</span>';
      html += '<span class="dp-group-count">' + gIssues.length + '</span>';
      html += '<span class="dp-group-arrow">&#9660;</span></div>';
      html += '<div class="dp-group-body open">';
      gIssues.forEach(issue => { html += renderIssueCard(issue); });
      html += '</div></div>';
    }
  }

  // Empty
  if (issues.length === 0) {
    html += '<div class="dp-empty-state"><div class="dp-empty-icon">&#9989;</div>';
    html += '<div class="dp-empty-title">Document Looks Good!</div>';
    html += '<div class="dp-empty-desc">No issues detected by any of the four AI checks.</div></div>';
  }

  // Re-analyze button
  html += '<button class="dp-reanalyze-btn" id="dpReanalyzeBtn">&#128260; Re-Analyze Document</button>';

  content.innerHTML = html;
  panel.style.display = "block";

  // ── Attach handlers ──

  content.querySelectorAll(".dp-group-header").forEach((hdr: HTMLElement) => {
    hdr.onclick = () => {
      const body = hdr.nextElementSibling as HTMLElement;
      const arrow = hdr.querySelector(".dp-group-arrow");
      if (body) body.classList.toggle("open");
      if (arrow) arrow.classList.toggle("open");
    };
  });

  content.querySelectorAll(".dp-issue-header").forEach((hdr: HTMLElement) => {
    hdr.onclick = (e) => {
      e.stopPropagation();
      const detail = hdr.nextElementSibling as HTMLElement;
      const arrow = hdr.querySelector(".dp-issue-arrow");
      if (!detail) return;
      const isOpen = detail.classList.contains("open");
      content.querySelectorAll(".dp-issue-detail.open").forEach((d: HTMLElement) => d.classList.remove("open"));
      content.querySelectorAll(".dp-issue-arrow.open").forEach((a: HTMLElement) => a.classList.remove("open"));
      if (!isOpen) {
        detail.classList.add("open");
        if (arrow) arrow.classList.add("open");
      }
    };
  });

  content.querySelectorAll(".dp-navigate-btn").forEach((btn: HTMLElement) => {
    btn.onclick = async (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.getAttribute("data-para-index") || "-1", 10);
      if (idx >= 0) {
        btn.textContent = "...";
        await navigateToParagraph(idx);
        btn.textContent = "Go to ▶";
      }
    };
  });

  content.querySelectorAll(".dp-autofix-single-btn").forEach((btn: HTMLElement) => {
    btn.onclick = async (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.getAttribute("data-para-index") || "-1", 10);
      const style = btn.getAttribute("data-expected-style") || "";
      if (idx < 0 || !style) return;
      btn.textContent = "Fixing...";
      (btn as HTMLButtonElement).disabled = true;
      const ok = await fixSingleIssue(idx, style);
      btn.textContent = ok ? "\u2713 Fixed" : "Failed";
      if (ok) {
        btn.style.background = "#16a34a";
        const card = btn.closest(".dp-issue-card") as HTMLElement;
        if (card) {
          card.style.opacity = "0.5";
          card.style.borderLeftColor = "#22c55e";
        }
      }
    };
  });

  const reBtn = document.getElementById("dpReanalyzeBtn");
  if (reBtn) reBtn.onclick = () => {
    dpHasAnalyzed = false; dpLastDeepAnalysis = null; dpAnalysisStale = false;
    panel.style.display = "none";
    runAnalysis();
  };

  const staleBtn = document.getElementById("dpStaleReanalyze");
  if (staleBtn) staleBtn.onclick = () => {
    dpHasAnalyzed = false; dpLastDeepAnalysis = null; dpAnalysisStale = false;
    panel.style.display = "none";
    runAnalysis();
  };

  if (dpAnalysisStale) {
    const banner = document.getElementById("dpStaleBanner");
    if (banner) banner.style.display = "flex";
  }
}

// ═══════════════════════════════════════════════════════════════
// ISSUE CARD — with source badge and source-specific extras
// ═══════════════════════════════════════════════════════════════

function renderIssueCard(issue: AIIssue): string {
  const paraIdx = (issue.paragraphIndex !== undefined && issue.paragraphIndex !== null)
    ? issue.paragraphIndex : -1;

  let html = '<div class="dp-issue-card ' + issue.severity + '" data-severity="' + issue.severity + '" data-source="' + (issue.source || "analyze") + '">';

  // Header
  html += '<div class="dp-issue-header">';
  html += '<span class="dp-issue-severity ' + issue.severity + '">' + issue.severity + '</span>';

  // Source badge — distinct color per origin
if (issue.source && issue.source !== "analyze") {
    const src: string = issue.source;        // widen to string
    const sourceLabel =
      src === "structure"   ? "STRUCT" :
      src === "terminology" ? "TERM"   :
      src === "crossref"    ? "X-REF"  :
      src.toUpperCase();
    html += '<span class="dp-source-badge dp-source-' + src + '">' + sourceLabel + '</span>';
  }

  html += '<span class="dp-issue-title">' + esc(issue.suggestedAction || issue.issueType) + '</span>';
  html += '<span class="dp-issue-confidence">' + Math.round((issue.confidence || 0) * 100) + '%</span>';
  html += '<span class="dp-issue-arrow">&#9660;</span>';
  html += '</div>';

  // Detail
  html += '<div class="dp-issue-detail">';

  // Paragraph text + navigate
  if (issue.paragraphText) {
    html += '<div class="dp-issue-text-row">';
    html += '<div class="dp-issue-text" style="flex:1">&#182; ' + esc(issue.paragraphText.substring(0, 150));
    if (issue.paragraphText.length > 150) html += '...';
    html += '</div>';
    if (paraIdx >= 0) {
      html += '<button class="dp-navigate-btn" data-para-index="' + paraIdx + '" title="Navigate to this paragraph in Word">Go to &#9654;</button>';
    }
    html += '</div>';
  }

  // Reasoning
  if (issue.reasoning) {
    html += '<div class="dp-issue-reasoning">' + esc(issue.reasoning) + '</div>';
  }

  // ── Source-specific extras ──

  // Structure: list affected items
  if (issue.source === "structure" && issue.affectedItems && issue.affectedItems.length > 0) {
    html += '<div class="dp-issue-meta"><span class="dp-meta-tag">Affected:</span> ';
    html += issue.affectedItems.map(a => '<span class="dp-meta-tag">' + esc(a) + '</span>').join(" ");
    html += '</div>';
  }

  // Terminology: list occurrences
  if (issue.source === "terminology") {
    if (issue.term) {
      html += '<div class="dp-issue-meta"><span class="dp-meta-tag">Term: ' + esc(issue.term) + '</span>';
      if (issue.definitionParagraph !== undefined && issue.definitionParagraph !== null) {
        html += '<span class="dp-meta-tag">Defined at para #' + issue.definitionParagraph + '</span>';
      }
      html += '</div>';
    }
    if (issue.occurrences && issue.occurrences.length > 0) {
      html += '<div class="dp-issue-meta"><span class="dp-meta-tag">Occurrences:</span> ';
      html += issue.occurrences.slice(0, 8).map(o =>
        '<button class="dp-occurrence-btn dp-navigate-btn" data-para-index="' + o + '">#' + o + '</button>'
      ).join(" ");
      if (issue.occurrences.length > 8) html += ' <span>+' + (issue.occurrences.length - 8) + '</span>';
      html += '</div>';
    }
  }

  // Cross-ref: show reference target + nearest existing
  if (issue.source === "crossref") {
    if (issue.referenceText) {
      html += '<div class="dp-issue-meta">';
      html += '<span class="dp-meta-tag">Reference: ' + esc(issue.referenceText) + '</span>';
      html += '<span class="dp-meta-tag">Target ' + (issue.targetExists ? "exists (ambiguous)" : "MISSING") + '</span>';
      html += '</div>';
    }
    if (issue.nearestExisting && issue.nearestExisting.length > 0) {
      html += '<div class="dp-issue-meta"><span class="dp-meta-tag">Nearest existing:</span> ';
      html += issue.nearestExisting.slice(0, 4).map(n => '<span class="dp-meta-tag">' + esc(n) + '</span>').join(" ");
      html += '</div>';
    }
  }

  // Default meta tags (style info)
  if (issue.source === "analyze" || !issue.source) {
    html += '<div class="dp-issue-meta">';
    if (issue.currentStyle && issue.currentStyle !== "—") html += '<span class="dp-meta-tag">Current: ' + esc(issue.currentStyle) + '</span>';
    if (issue.expectedStyle && issue.expectedStyle !== "—") html += '<span class="dp-meta-tag">Expected: ' + esc(issue.expectedStyle) + '</span>';
    if (paraIdx >= 0) html += '<span class="dp-meta-tag">Para #' + paraIdx + '</span>';
    html += '</div>';
  }

  // Auto-fix button (only for analyze-source issues with autoFixable)
  if (issue.autoFixable && issue.expectedStyle && issue.expectedStyle !== "—" && paraIdx >= 0) {
    html += '<button class="dp-autofix-single-btn" data-para-index="' + paraIdx + '" data-expected-style="' + esc(issue.expectedStyle) + '">';
    html += '&#9889; Fix this: Apply "' + esc(issue.expectedStyle) + '" to para #' + paraIdx;
    html += '</button>';
  }

  html += '</div></div>';
  return html;
}

// ═══════════════════════════════════════════════════════════════
// FILTER
// ═══════════════════════════════════════════════════════════════

function filterIssues(filter: string): void {
  document.querySelectorAll(".dp-issue-card").forEach((card: HTMLElement) => {
    card.style.display = (filter === "all" || card.getAttribute("data-severity") === filter) ? "" : "none";
  });
  document.querySelectorAll(".dp-issue-group").forEach((group: HTMLElement) => {
    const visible = group.querySelectorAll('.dp-issue-card:not([style*="display: none"])');
    group.style.display = (visible.length > 0 || filter === "all") ? "" : "none";
  });
}

// ═══════════════════════════════════════════════════════════════
// REPORT — cached (unchanged)
// ═══════════════════════════════════════════════════════════════

async function onReportClick(): Promise<void> {
  if (dpLastReportResult) {
    const p = document.getElementById("dp-report-panel");
    if (p) { p.style.display = "block"; return; }
  }
  await runReport();
}

async function runReport(): Promise<void> {
  if (!dpConnected) { await checkConnection(); if (!dpConnected) return; }
  const panel = document.getElementById("dp-report-panel");
  const content = document.getElementById("dpReportContent");
  if (!panel || !content) return;

  content.innerHTML = '<div class="dp-analyzing-state"><div class="dp-analyzing-icon"></div><div class="dp-analyzing-text">Generating report...</div></div>';
  panel.style.display = "block";

  try {
    const qcResults: any[] = [];
    for (const [id, name] of Object.entries(DP_CHECK_NAMES)) {
      const badge = document.getElementById("status-" + id);
      let status = "pending", message = "";
      if (badge) {
        if (badge.classList.contains("done")) { status = "done"; message = badge.title || "Completed"; }
        else if (badge.classList.contains("error")) { status = "error"; message = badge.title || "Error"; }
      }
      qcResults.push({ name, status, message, timestamp: null });
    }
    const reportResult = await generateReport(qcResults);
    dpLastReportResult = reportResult;
    renderReport(reportResult, content);
  } catch (err) {
    content.innerHTML =
      '<div class="dp-empty-state"><div class="dp-empty-icon">&#9888;&#65039;</div>' +
      '<div class="dp-empty-title">Report Failed</div>' +
      '<div class="dp-empty-desc">' + esc((err as Error).message || "") + '</div></div>';
  }
}

function renderReport(result: AIReportResult, container: HTMLElement): void {
  const rpt = result.report;
  let html = '<div class="dp-report-status ' + (rpt.overallStatus || "pass") + '">';
  html += rpt.overallStatus === "pass" ? "&#9989; All Checks Passed"
    : rpt.overallStatus === "pass_with_warnings" ? "&#9888;&#65039; Passed with Warnings"
    : rpt.overallStatus === "needs_review" ? "&#128269; Needs Review"
    : "&#10060; Issues Detected";
  html += '</div>';

  if (rpt.statsBar) {
    const s = rpt.statsBar;
    html += '<div class="dp-summary-bar">';
    html += '<div class="dp-summary-stat"><span class="dp-summary-stat-num" style="color:#0078d4">' + s.completed + '</span><span class="dp-summary-stat-label">Completed</span></div>';
    html += '<div class="dp-summary-stat"><span class="dp-summary-stat-num dp-stat-auto">' + s.passed + '</span><span class="dp-summary-stat-label">Passed</span></div>';
    html += '<div class="dp-summary-stat"><span class="dp-summary-stat-num dp-stat-warning">' + s.warnings + '</span><span class="dp-summary-stat-label">Warnings</span></div>';
    html += '<div class="dp-summary-stat"><span class="dp-summary-stat-num" style="color:#999">' + s.skipped + '</span><span class="dp-summary-stat-label">Skipped</span></div></div>';
  }
  if (rpt.executiveSummary) html += '<div class="dp-report-executive">' + esc(rpt.executiveSummary) + '</div>';
  if (rpt.sections) {
    rpt.sections.forEach(sec => {
      html += '<div class="dp-report-section"><div class="dp-report-section-title">' + esc(sec.title) + '</div>';
      sec.checks.forEach(ch => {
        const ico = ch.status === "done" ? "&#9989;" : ch.status === "error" ? "&#9888;&#65039;" : "&#9723;&#65039;";
        html += '<div class="dp-report-check-row"><span class="dp-report-run-num">' + ch.run + '</span><span>' + ico + '</span><span class="dp-report-check-name">' + esc(ch.name) + '</span><span class="dp-report-check-summary">' + esc(ch.summary || "") + '</span></div>';
      });
      html += '</div>';
    });
  }
  if (rpt.recommendations?.length) {
    html += '<div class="dp-report-recommendations"><h4>Recommendations</h4>';
    rpt.recommendations.forEach(r => { html += '<div class="dp-report-rec-item">' + esc(r) + '</div>'; });
    html += '</div>';
  }
  container.innerHTML = html;
}

// ═══════════════════════════════════════════════════════════════
// VIEW JSON
// ═══════════════════════════════════════════════════════════════

function showJSONPanel(): void {
  const panel = document.getElementById("dp-json-panel");
  const content = document.getElementById("dpJsonContent");
  if (!panel || !content) return;

  const lastJSON = getLastExtractedJSON();
  if (!lastJSON) {
    content.innerHTML = '<div class="dp-empty-state"><div class="dp-empty-icon">&#128196;</div><div class="dp-empty-title">No JSON Yet</div><div class="dp-empty-desc">Run "Analyze Document" first.</div></div>';
  } else {
    const jsonStr = JSON.stringify(lastJSON, null, 2);
    const sizeKB = (new Blob([jsonStr]).size / 1024).toFixed(1);
    content.innerHTML = '<div class="dp-meta-bar"><span><strong>' + (lastJSON.paragraphs?.length || 0) + '</strong> paragraphs, <strong>' + (lastJSON.tables?.length || 0) + '</strong> tables</span><span>' + sizeKB + ' KB</span></div><pre class="dp-json-pre">' + esc(jsonStr) + '</pre>';
  }
  panel.style.display = "block";
}

// ═══════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════

function esc(text: string): string {
  if (!text) return "";
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}